import { appAccessEvaluator } from '@electron/shared/app-access-policy';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  type AppWithExclusionState,
  type CategoryID,
  type CategoryInfo,
  DEFAULT_ENABLED_CATEGORY_IDS,
  electronApis,
} from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';
import { inElectron } from '@/utils';
import { listenWithAbortIPC } from '@/utils/abort';

import type {
  AppAccessMode,
  AppAccessPolicy,
  AppPickerErrorCode,
} from '@electron/shared/types';

const APP_PICKER_ERROR_COPY: Partial<Record<AppPickerErrorCode, string>> = {
  installed_apps_unavailable:
    "Couldn't read your installed apps. Please try again.",
  unreadable_app:
    "Couldn't read that app. Choose an app from your Applications folder.",
};

export const lockedByLabel = (lockedBy: AppWithExclusionState['lockedBy']) =>
  lockedBy === 'team' ? '(excluded by your team)' : '(excluded by default)';

export const isExclusionSetupCustomized = (
  apps: AppWithExclusionState[],
  enabledCategoryIds: CategoryID[]
) =>
  apps.some(
    (app) => app.canUserModify && app.isExcluded !== app.isDefaultExclusion
  ) ||
  enabledCategoryIds.length !== DEFAULT_ENABLED_CATEGORY_IDS.length ||
  DEFAULT_ENABLED_CATEGORY_IDS.some((id) => !enabledCategoryIds.includes(id));

type PendingAppEdits = ReadonlyMap<string, boolean>;

const applyEdits = (
  apps: AppWithExclusionState[],
  edits: PendingAppEdits
): AppWithExclusionState[] =>
  edits.size === 0
    ? apps
    : apps.map((app) => {
        const desired = edits.get(app.identifier);
        return desired === undefined || desired === app.isExcluded
          ? app
          : { ...app, isExcluded: desired };
      });

const useRevision = () => {
  const revision = useRef(0);
  return useMemo(
    () => ({
      begin: () => ++revision.current,
      isCurrent: (token: number) => token === revision.current,
      invalidate: () => {
        revision.current += 1;
      },
    }),
    []
  );
};

const useWriteQueue = (onTailFailure: () => void) => {
  const chain = useRef<Promise<boolean>>(Promise.resolve(true));
  const onTailFailureRef = useRef(onTailFailure);
  onTailFailureRef.current = onTailFailure;

  const enqueue = useCallback((work: () => Promise<boolean>) => {
    const run = chain.current.then(work, work);
    const settled = run.catch(() => false);
    chain.current = settled;
    settled.then((ok) => {
      if (!ok && chain.current === settled) onTailFailureRef.current();
    });
    return run;
  }, []);

  const flush = useCallback(async () => {
    let tail = chain.current;
    let ok = await tail;
    while (tail !== chain.current) {
      tail = chain.current;
      ok = await tail;
    }
    if (!ok) chain.current = Promise.resolve(true);
    return ok;
  }, []);

  return { enqueue, flush };
};

export interface AppExclusions {
  apps: AppWithExclusionState[];
  appAccessMode: AppAccessMode;
  appAccessModeLockedByTeam: boolean;
  loading: boolean;
  loadFailed: boolean;
  error: string | null;
  appAccessModeError: string | null;
  savingAppAccessMode: boolean;
  reloadApps: () => Promise<void>;
  toggleApp: (identifier: string) => Promise<boolean>;
  setAppAccessMode: (mode: AppAccessMode) => Promise<boolean>;
  flushAppWrites: () => Promise<boolean>;
  addAppManually: () => Promise<void>;
  addingAppManually: boolean;
}

export const useAppExclusions = (enabled = true): AppExclusions => {
  const [persistedApps, setPersistedApps] = useState<AppWithExclusionState[]>(
    []
  );
  const [pendingEdits, setPendingEdits] = useState<PendingAppEdits>(new Map());
  const [appAccessMode, setAppAccessModeState] =
    useState<AppAccessMode>('block');
  const [appAccessModeLockedByTeam, setAppAccessModeLockedByTeam] =
    useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appAccessModeError, setAppAccessModeError] = useState<string | null>(
    null
  );
  const [savingAppAccessMode, setSavingAppAccessMode] = useState(false);
  const savingModeRef = useRef(false);
  const [addingAppManually, setAddingAppManually] = useState(false);
  const revision = useRevision();

  const persistedRef = useRef(persistedApps);
  persistedRef.current = persistedApps;
  const pendingRef = useRef(pendingEdits);
  pendingRef.current = pendingEdits;

  const apps = useMemo(
    () => applyEdits(persistedApps, pendingEdits),
    [persistedApps, pendingEdits]
  );

  const setPersisted = useCallback((next: AppWithExclusionState[]) => {
    persistedRef.current = next;
    setPersistedApps(next);
  }, []);

  const setPending = useCallback((next: PendingAppEdits) => {
    pendingRef.current = next;
    setPendingEdits(next);
  }, []);

  const loadApps = useCallback(
    async ({ keepError = false } = {}) => {
      const token = revision.begin();
      try {
        const [loadedApps, policy] = await Promise.all([
          electronApis.appExclusion.getAppExclusions(),
          electronApis.appExclusion.getAppAccessPolicy(),
        ]);
        if (!revision.isCurrent(token)) return;
        setPersisted(loadedApps);
        setAppAccessModeState(policy.appAccessMode);
        setAppAccessModeLockedByTeam(!!policy.appAccessModeLockedByTeam);
        setLoadFailed(false);
        if (!keepError) setError(null);
      } catch (err) {
        if (!revision.isCurrent(token)) return;
        setLoadFailed(true);
        logError(err, 'Failed to load app exclusions');
      } finally {
        if (revision.isCurrent(token)) setLoaded(true);
      }
    },
    [revision, setPersisted]
  );

  const reloadApps = useCallback(() => loadApps(), [loadApps]);

  const { enqueue: enqueueWrite, flush: flushAppWrites } = useWriteQueue(() => {
    setPending(new Map());
    loadApps({ keepError: true });
  });

  const active = enabled && inElectron();

  useEffect(() => {
    if (!active) return;
    loadApps();
    const unsubscribe = listenWithAbortIPC('exclusions-updated', () =>
      loadApps()
    );
    return () => {
      unsubscribe?.();
      revision.invalidate();
    };
  }, [active, loadApps, revision]);

  const writePendingEdits = useCallback(async () => {
    const written = new Map(pendingRef.current);
    try {
      for (const [id, excluded] of written) {
        const result = await electronApis.appExclusion.setAppExcluded(
          id,
          excluded
        );
        if (!result.success) {
          throw new Error(result.error ?? 'update failed');
        }
      }
    } catch (err) {
      setError('Could not update that app. Please try again.');
      logError(err, 'Failed to update app exclusion');
      return false;
    }
    revision.invalidate();
    setPersisted(applyEdits(persistedRef.current, written));
    const remaining = new Map(pendingRef.current);
    for (const [id, desired] of written) {
      if (remaining.get(id) === desired) remaining.delete(id);
    }
    setPending(remaining);
    setError(null);
    return true;
  }, [revision, setPending, setPersisted]);

  const toggleApp = useCallback(
    async (identifier: string) => {
      if (savingModeRef.current) return true;
      const current = applyEdits(persistedRef.current, pendingRef.current).find(
        (app) => app.identifier === identifier
      );
      if (!current?.canUserModify) return true;

      const edits = new Map(pendingRef.current);
      edits.set(identifier, !current.isExcluded);
      setPending(edits);

      return enqueueWrite(writePendingEdits);
    },
    [enqueueWrite, setPending, writePendingEdits]
  );

  const applyPolicy = useCallback(
    (policy: AppAccessPolicy) => {
      const isExcluded = appAccessEvaluator(policy);
      revision.invalidate();
      setPersisted(
        persistedRef.current.map((app) => ({
          ...app,
          isExcluded: isExcluded(app.identifier),
        }))
      );
      setAppAccessModeState(policy.appAccessMode);
      setAppAccessModeLockedByTeam(!!policy.appAccessModeLockedByTeam);
    },
    [revision, setPersisted]
  );

  const setAppAccessMode = useCallback(
    (mode: AppAccessMode) => {
      savingModeRef.current = true;
      setSavingAppAccessMode(true);
      return enqueueWrite(async () => {
        try {
          if (!(await writePendingEdits())) return false;
          const result = await electronApis.appExclusion.setAppAccessMode(mode);
          if (!result.success || !result.policy) {
            throw new Error(result.error ?? 'mode update failed');
          }
          applyPolicy(result.policy);
          setAppAccessModeError(null);
          return true;
        } catch (err) {
          setAppAccessModeError(
            'Could not change which apps Littlebird sees. Please try again.'
          );
          logError(err, 'Failed to set app access mode');
          return false;
        } finally {
          savingModeRef.current = false;
          setSavingAppAccessMode(false);
        }
      });
    },
    [applyPolicy, enqueueWrite, writePendingEdits]
  );

  const addAppManually = useCallback(async () => {
    setAddingAppManually(true);
    try {
      await flushAppWrites();
      const result = await electronApis.appExclusion.openDynamicAppPicker();
      if (!result || result.code === 'no_app_selected') return;
      if (!result.success) {
        setError(
          (result.code && APP_PICKER_ERROR_COPY[result.code]) ??
            "Couldn't add that app. Please try again."
        );
        logError(result.error, 'Failed to add app manually');
        return;
      }
      if (result.data) {
        revision.invalidate();
        setPersisted(result.data);
        setLoadFailed(false);
        setLoaded(true);
      }
      setError(null);
    } catch (err) {
      setError("Couldn't add that app. Please try again.");
      logError(err, 'Failed to add app manually');
    } finally {
      setAddingAppManually(false);
    }
  }, [flushAppWrites, revision, setPersisted]);

  return {
    apps,
    appAccessMode,
    appAccessModeLockedByTeam,
    loading: active && !loaded,
    loadFailed,
    error,
    appAccessModeError,
    savingAppAccessMode,
    reloadApps,
    toggleApp,
    setAppAccessMode,
    flushAppWrites,
    addAppManually,
    addingAppManually,
  };
};

export interface CategoryExclusions {
  categories: CategoryInfo[];
  enabledCategoryIds: CategoryID[];
  categoriesLoading: boolean;
  categoriesLoadFailed: boolean;
  categoriesError: string | null;
  reloadCategories: () => Promise<void>;
  toggleCategory: (id: CategoryID) => Promise<boolean>;
  flushCategoryWrites: () => Promise<boolean>;
}

export const useCategoryExclusions = (enabled = true): CategoryExclusions => {
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [persistedIds, setPersistedIds] = useState<CategoryID[]>([]);
  const [pendingIds, setPendingIds] = useState<CategoryID[] | null>(null);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [categoriesLoadFailed, setCategoriesLoadFailed] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const revision = useRevision();

  const persistedRef = useRef(persistedIds);
  persistedRef.current = persistedIds;
  const pendingRef = useRef(pendingIds);
  pendingRef.current = pendingIds;

  const setPersisted = useCallback((next: CategoryID[]) => {
    persistedRef.current = next;
    setPersistedIds(next);
  }, []);

  const setPending = useCallback((next: CategoryID[] | null) => {
    pendingRef.current = next;
    setPendingIds(next);
  }, []);

  const loadCategories = useCallback(async () => {
    const token = revision.begin();
    try {
      const [result, ids] = await Promise.all([
        electronApis.categoryExclusion.getCategoryExclusions(),
        electronApis.categoryExclusion.getEnabledCategoryIds(),
      ]);
      if (!revision.isCurrent(token)) return;
      if (!result.success || !result.categories?.length) {
        throw new Error(result.error ?? 'no categories returned');
      }
      setCategories(result.categories);
      setPersisted(ids);
      setCategoriesLoadFailed(false);
      setCategoriesError(null);
    } catch (err) {
      if (!revision.isCurrent(token)) return;
      setCategoriesLoadFailed(true);
      logError(err, 'Failed to load category exclusions');
    } finally {
      if (revision.isCurrent(token)) setCategoriesLoaded(true);
    }
  }, [revision, setPersisted]);

  const resyncEnabledIds = useCallback(async () => {
    setPending(null);
    const token = revision.begin();
    try {
      const restored =
        await electronApis.categoryExclusion.getEnabledCategoryIds();
      if (revision.isCurrent(token)) setPersisted(restored);
    } catch (err) {
      logError(err, 'Failed to reload enabled categories');
    }
  }, [revision, setPending, setPersisted]);

  const { enqueue: enqueueWrite, flush: flushCategoryWrites } =
    useWriteQueue(resyncEnabledIds);

  const active = enabled && inElectron();

  useEffect(() => {
    if (!active) return;
    loadCategories();
    return () => revision.invalidate();
  }, [active, loadCategories, revision]);

  const toggleCategory = useCallback(
    async (id: CategoryID) => {
      const base = pendingRef.current ?? persistedRef.current;
      const next = base.includes(id)
        ? base.filter((c) => c !== id)
        : [...base, id];
      setPending(next);

      return enqueueWrite(async () => {
        const desired = pendingRef.current ?? persistedRef.current;
        try {
          const result =
            await electronApis.categoryExclusion.updateCategoryExclusions(
              desired
            );
          if (!result.success) throw new Error(result.error ?? 'update failed');
          revision.invalidate();
          setPersisted(desired);
          if (pendingRef.current === desired) setPending(null);
          setCategoriesError(null);
          return true;
        } catch (err) {
          setCategoriesError(
            'Could not update that category. Please try again.'
          );
          logError(err, 'Failed to update category exclusions');
          return false;
        }
      });
    },
    [enqueueWrite, revision, setPending, setPersisted]
  );

  return {
    categories,
    enabledCategoryIds: pendingIds ?? persistedIds,
    categoriesLoading: active && !categoriesLoaded,
    categoriesLoadFailed,
    categoriesError,
    reloadCategories: loadCategories,
    toggleCategory,
    flushCategoryWrites,
  };
};

type ExclusionsContextValue = AppExclusions &
  CategoryExclusions & {
    isCustomized: boolean;
    flushWrites: () => Promise<boolean>;
  };

const AppExclusionsContext = createContext<ExclusionsContextValue | null>(null);

export const AppExclusionsProvider = ({
  enabled = true,
  children,
}: {
  enabled?: boolean;
  children: ReactNode;
}) => {
  const appExclusions = useAppExclusions(enabled);
  const categoryExclusions = useCategoryExclusions(enabled);

  const flushWrites = async () => {
    const [appsSaved, categoriesSaved] = await Promise.all([
      appExclusions.flushAppWrites(),
      categoryExclusions.flushCategoryWrites(),
    ]);
    return appsSaved && categoriesSaved;
  };

  const value = {
    ...appExclusions,
    ...categoryExclusions,
    isCustomized: isExclusionSetupCustomized(
      appExclusions.apps,
      categoryExclusions.enabledCategoryIds
    ),
    flushWrites,
  };

  return (
    <AppExclusionsContext.Provider value={value}>
      {children}
    </AppExclusionsContext.Provider>
  );
};

export const useAppExclusionsContext = (): ExclusionsContextValue => {
  const context = useContext(AppExclusionsContext);
  if (!context) {
    throw new Error(
      'useAppExclusionsContext must be used within an AppExclusionsProvider'
    );
  }
  return context;
};
